/**
 * RevokePage — employee-facing right-to-be-forgotten request surface.
 *
 * Auth-gated: the caller must be signed in and the Profile they're requesting
 * deletion for must match their authenticated email. Per Handoff §7a: legal
 * hold takes precedence; otherwise the Profile is purged within 30 days.
 */

import { useState, useEffect } from "react";
import { useSession } from "../lib/useSession.js";
import { supabase } from "../lib/supabase.js";
import { Wordmark, SectionHeader, Callout } from "../components/index.js";

export default function RevokePage() {
  const { session, loading } = useSession();
  const [profiles, setProfiles] = useState([]);
  const [error, setError] = useState(null);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  useEffect(() => {
    if (!session?.user?.email) return;
    // Find Profiles whose work_email matches this user's auth email
    const email = session.user.email.toLowerCase();
    supabase
      .from("predisposition_profiles")
      .select("internal_uuid, display_name, full_name, work_email, mode, departure_date, archive_eligible_after")
      .ilike("work_email", email)
      .then(({ data, error }) => {
        if (error) setError(error.message);
        else setProfiles(data || []);
      });
  }, [session]);

  const requestDeletion = async (profile) => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch("/.netlify/functions/profile-deletion-request", {
        method: "POST",
        headers: { "content-type": "application/json", authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ profile_id: profile.internal_uuid, reason }),
      });
      const body = await res.json();
      if (!res.ok) {
        setError(body.error || "Request failed");
      } else {
        setSubmitted({ profile, response: body });
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <p style={{ padding: 24 }}>Loading…</p>;
  if (!session) {
    return (
      <div style={{ maxWidth: 560, margin: "60px auto", padding: "0 24px" }}>
        <Wordmark scale={1.0} />
        <h1 style={titleStyle}>Sign in to revoke or delete your Profile</h1>
        <p style={paragraphStyle}>
          Right-to-be-forgotten requests require sign-in so we can verify the Profile belongs to you. If someone set up your Profile for you, sign in with the same email address the invite was sent to.
        </p>
      </div>
    );
  }

  if (submitted) {
    return (
      <div style={{ maxWidth: 600, margin: "60px auto", padding: "0 24px" }}>
        <Wordmark scale={1.0} />
        <h1 style={titleStyle}>Deletion requested.</h1>
        <Callout.Tip>
          {submitted.response.message}
        </Callout.Tip>
        <p style={paragraphStyle}>
          The deletion fires on <strong>{new Date(submitted.response.deletion_fires_at).toLocaleDateString()}</strong>. If the sender flags an active legal hold during the 30-day window, deletion is paused and you'll receive a separate notification.
        </p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 720, margin: "0 auto", padding: "32px 24px" }}>
      <div style={{ marginBottom: 24 }}>
        <Wordmark scale={1.0} />
      </div>

      <SectionHeader index={1} title="Revoke or delete your Profile" />

      <Callout.Remember>
        You can revoke consent or request deletion of your Interaction Engineering Profile at any time. Deletion fires within 30 days unless an active legal hold applies (active investigation or ongoing claim — the sender controls that flag).
      </Callout.Remember>

      {error && (
        <div style={errorStyle}>
          {error}
        </div>
      )}

      {profiles.length === 0 ? (
        <p style={paragraphStyle}>
          No Profiles in Interaction Engineering match the email address you're signed in with. If you believe a Profile exists for you under a different email, contact the sender.
        </p>
      ) : (
        <div style={{ display: "grid", gap: 16, marginTop: 24 }}>
          {profiles.map((p) => (
            <article key={p.internal_uuid} style={profileCardStyle}>
              <div style={{
                fontFamily: "var(--ppr-typography-font-family-display)",
                fontSize: "20px",
                color: "var(--ppr-color-accent-navy)",
                marginBottom: 4,
              }}>
                {p.display_name || p.full_name}
              </div>
              <div style={{ fontFamily: "var(--ppr-typography-font-family-mono)", fontSize: "12px", color: "var(--ppr-color-text-muted)", marginBottom: 12 }}>
                Mode: {p.mode}{p.archive_eligible_after ? ` · scheduled to delete ${new Date(p.archive_eligible_after).toLocaleDateString()}` : ""}
              </div>
              {p.mode === "LegalHold" ? (
                <Callout.WatchOut>
                  This Profile is under active legal hold. Deletion cannot be requested until the hold is released. Contact the sender for more information.
                </Callout.WatchOut>
              ) : p.mode === "Departed" && p.archive_eligible_after ? (
                <Callout.Tip>
                  Deletion is already scheduled. You don't need to take any further action.
                </Callout.Tip>
              ) : (
                <>
                  <label style={{
                    display: "block",
                    fontFamily: "var(--ppr-typography-font-family-display)",
                    fontSize: "var(--ppr-typography-font-size-sm)",
                    letterSpacing: "var(--ppr-typography-letter-spacing-wider)",
                    textTransform: "uppercase",
                    color: "var(--ppr-color-text-secondary)",
                    marginTop: 12,
                    marginBottom: 6,
                  }}>
                    Reason (optional)
                  </label>
                  <textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    rows={3}
                    placeholder="You can leave this blank. We don't share your reason with your employer."
                    style={inputStyle}
                  />
                  <button
                    onClick={() => requestDeletion(p)}
                    disabled={submitting}
                    style={dangerBtn}
                  >
                    {submitting ? "Requesting…" : "Request deletion"}
                  </button>
                </>
              )}
            </article>
          ))}
        </div>
      )}
    </div>
  );
}

const titleStyle = {
  fontFamily: "var(--ppr-typography-font-family-display)",
  fontSize: "var(--ppr-typography-font-size-3xl)",
  color: "var(--ppr-color-accent-navy)",
  letterSpacing: "var(--ppr-typography-letter-spacing-snug)",
  margin: "16px 0 12px",
};
const paragraphStyle = {
  fontFamily: "var(--ppr-typography-font-family-body)",
  fontSize: "var(--ppr-typography-font-size-base)",
  lineHeight: "var(--ppr-typography-line-height-relaxed)",
  color: "var(--ppr-color-text-secondary)",
  marginBottom: 20,
};
const inputStyle = {
  width: "100%",
  padding: "10px 12px",
  fontFamily: "var(--ppr-typography-font-family-body)",
  fontSize: "var(--ppr-typography-font-size-base)",
  border: "1px solid var(--ppr-color-border-default)",
  background: "var(--ppr-color-background-surface)",
  color: "var(--ppr-color-text-primary)",
  outline: "none",
  resize: "vertical",
  marginBottom: 12,
};
const profileCardStyle = {
  padding: "18px 20px",
  border: "1px solid var(--ppr-color-border-default)",
  background: "var(--ppr-color-background-surface)",
};
const dangerBtn = {
  padding: "12px 18px",
  fontFamily: "var(--ppr-typography-font-family-display)",
  fontSize: "var(--ppr-typography-font-size-sm)",
  letterSpacing: "var(--ppr-typography-letter-spacing-wider)",
  textTransform: "uppercase",
  border: "2px solid var(--ppr-color-accent-coral)",
  background: "var(--ppr-color-accent-coral)",
  color: "var(--ppr-color-text-inverse)",
  cursor: "pointer",
};
const errorStyle = {
  padding: "12px 14px",
  border: "2px solid var(--ppr-color-accent-coral)",
  background: "var(--ppr-color-accent-coral-very-soft)",
  color: "var(--ppr-color-accent-coral-dark)",
  marginBottom: 16,
};
